/* eslint-disable no-param-reassign */

import { Inject, Injectable, NgZone } from '@angular/core';
import { WINDOW } from '@ng-web-apis/common';
import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';

import { CardinalService } from './cardinal.service';
import { PaymentExtraInfo, PaymentMethod, PaymentModeInfo } from './payment.model';

import { ApiOrderService } from '@api/_api/api-order/api-order.service';
import { ApiPaymentPayload, AsyncCheckoutOptions, PaymentMethodTypes } from '@api/_api/api-payment/api-payment.model';
import { BrowserOnly } from '@core/data-providers/platform/platform.decorators';
import { CommonWindow } from '@core/domain/models/common-window.model';
import { logPaymentError, logPaymentOk } from '@shared/analytics/ITXObservabilityCore/helper';
import { SetLoadedStatus } from '@shared/navigation-button/store/navigation-button.actions';

@Injectable()
export class PaymentService {
    constructor(
        private readonly apiOrderService: ApiOrderService,
        private readonly cardinalService: CardinalService,
        private readonly zone: NgZone,
        private readonly store: Store,
        @Inject(WINDOW) private readonly window: CommonWindow,
    ) {}

    // TODO ECOMDUTI-20991 tipos
    public getPaymentBundle(
        selectedMethod: PaymentMethod,
        paymentData: ApiPaymentPayload,
        extraInfo: PaymentExtraInfo,
        countryCode: string,
        urlOk: string,
        pageOk: string
    ): AsyncCheckoutOptions | any {
        const paymentBundle: AsyncCheckoutOptions = {};

        const cpaymentData = { ...paymentData };

        if (this.cardinalService.isCardinalMethod(selectedMethod) && cpaymentData.transactionId) {
            delete cpaymentData.transactionId;
        }

        paymentBundle.card = {
            paymentMethodType: cpaymentData.paymentMethodType,
            paymentMethodKind: cpaymentData.paymentMethodKind,
            number: cpaymentData.number,
            cvv2: cpaymentData.cvv2,
            month: cpaymentData.month,
            year: cpaymentData.year,
            vatin: cpaymentData.vatin,
            holder: cpaymentData.holder,
            paymentModeId: cpaymentData.paymentModeId,
            amount: cpaymentData.amount,
            balance: cpaymentData.balance,
            variant: cpaymentData.variant,
            transactionId: cpaymentData.transactionId,
            authorizationToken: cpaymentData.authorizationToken,
            cardHash: cpaymentData.cardHash,
            otp: cpaymentData.otp,
        };

        if (selectedMethod.kind === PaymentMethodTypes.PSE) {
            paymentBundle.card.bankId = cpaymentData.bankId;
            paymentBundle.card.documentNumber = cpaymentData.documentNumber;
            paymentBundle.card.documentType = cpaymentData.documentType;
            paymentBundle.card.personType = cpaymentData.personType;
            paymentBundle.card.phoneNumber = cpaymentData.phoneNumber;
            paymentBundle.card.prefixPhoneNumber = cpaymentData.prefixPhoneNumber;
        }

        if (selectedMethod.kind === PaymentMethodTypes.CREDIT_CARD_INSTALLMENTS_UNIQUEID) {
            paymentBundle.card.governmentId = cpaymentData.governmentId;
        }

        if (selectedMethod.kind === PaymentMethodTypes.P24 || selectedMethod.kind === PaymentMethodTypes.MULTIBANK) {
            cpaymentData.eShopperCode = countryCode;

            if (selectedMethod.kind === PaymentMethodTypes.P24) {
                paymentBundle.card.eShopperCode = cpaymentData.eShopperCode;
            }
        }

        if (selectedMethod.kind === PaymentMethodTypes.IDEAL) {
            paymentBundle.card.eShopperCode = cpaymentData.eShopperCode;
        }

        if (selectedMethod.kind === PaymentMethodTypes.UPI) {
            paymentBundle.card.unique_id = cpaymentData.unique_id;
        }

        if (selectedMethod.kind === PaymentMethodTypes.BLIK) {
            paymentBundle.card.otp = cpaymentData.otp;
        }

        if (extraInfo.repay) {
            paymentBundle.repayOrderId = Number(extraInfo.orderId);
            paymentBundle.repay = true;
        }
        paymentBundle.urlOk = urlOk;
        paymentBundle.pageOk = pageOk;

        return paymentBundle;
    }

    public getPaymentMode(
        storeId: any,
        langId: string,
        orderId: string,
        policy: string,
        cardNumber: string,
        vatin: string,
        orderAmount?: number
    ): Observable<PaymentModeInfo> {
        return this.apiOrderService.paymentModes(storeId, langId, orderId, policy, cardNumber, vatin, orderAmount);
    }

    @BrowserOnly<PaymentService, Observable<undefined>>(of(undefined))
    public submitPayment$(
        selectedMethod: PaymentMethod,
        cpaymentData: ApiPaymentPayload,
        countryCode: string,
        extraInfo: PaymentExtraInfo,
        urlOk: string,
        pageOk: string,
    ): Observable<any> {
        return new Observable((observer) => {
            const paymentObject: AsyncCheckoutOptions = this.getPaymentBundle(
                selectedMethod,
                cpaymentData,
                extraInfo,
                countryCode,
                urlOk,
                pageOk
            );

            paymentObject.orderId = extraInfo.orderId;

            paymentObject.onStart = (): void => {
                // ItxLoaderFct.show();
            };

            paymentObject.onReferred = (url: any, parameters: any): void => {
                const script = document.createElement('script');
                script.src = url.split('?')[0];
                script.type = 'text/javascript';
                const urlParams = new URLSearchParams(parameters.ReturnURL.split('?')[1]);
                parameters.plc = urlParams.get('plc');
                parameters.ack = urlParams.get('ack');
                parameters['chk-status'] = urlParams.get('chk-status');

                script.onload = () => {
                    const form = document.createElement('form');

                    Object.getOwnPropertyNames(parameters).forEach(name => {
                        const field = document.createElement('input');
                        field.type = 'hidden';
                        field.name = name;
                        field.value = parameters[name];
                        form.append(field);
                    });
                    document.body.append(form);

                    this.window.goPay(form);

                    this.window.nicepaySubmit = () => {
                        // limpiamos la url de parametros inline hasta que nos la den limpia
                        form.action = parameters.ReturnURL.split('?')[0];
                        form.submit();
                    };

                    this.window.nicepayClose = () => {
                        this.store.dispatch(SetLoadedStatus());
                    };
                };
                document.body.append(script);
            };

            paymentObject.onError = (errorKey: string, errorMessage: string): void => {
                this.zone.run(() => {
                    logPaymentError(paymentObject, { errorKey, errorMessage });
                    observer.error({ errorKey, errorMessage });
                });
            };

            paymentObject.onAuthAgain = (): void => {
                // NavigationService.goToAuthPage();
            };

            paymentObject.onChallenge = (ctx: any, callback): void => {
                // ItxLoaderFct.show();
                this.cardinalService.addStep2Listener(callback);
                this.cardinalService.iframeChallenge(ctx.params.JWT, ctx.url);
            };

            paymentObject.onPaymentSuccess = (): void => {
                this.zone.run(() => {
                    logPaymentOk(paymentObject);
                    observer.next(true);
                    observer.complete();
                });
            };

            if (extraInfo.invoice) {
                paymentObject.invoice = '1';
            } else {
                paymentObject.invoice = '0';
            }

            if (extraInfo.vatin) {
                paymentObject.vatin = extraInfo.vatin;
            }

            if (extraInfo.caritas) {
                paymentObject.isJoinLife = true;
            }

            if (extraInfo.repay) {
                paymentObject.repayOrderId = parseInt(extraInfo.orderId);
            }

            // Si es una sesión cárdinal, enviamos la propiedad ddc. 3DS2
            if (selectedMethod.sessionType && selectedMethod.sessionType === 'cardinal_session') {
                paymentObject.ddc = extraInfo.ddc;
            }

            // TODO ECOMDUTI-20991 url de ko
            paymentObject.urlKo = this.window.location.href;

            this.window.inditex.submitPayment(paymentObject);
        });
    }
}
